/**
 * Итог начисления с премиями и штрафами.
 *
 * Зачем отдельный файл ради трёх строк арифметики: одно и то же выражение
 * «премия со знаком плюс, штраф со знаком минус» было переписано в девяти
 * местах — в маппере ответа, в оплате, в отмене выплаты, в изменении, в
 * сводке списка, в дашборде, в личном кабинете игрока и дважды в форме.
 * Четыре из них разошлись между собой, и каждое расхождение обошлось в
 * отдельный баг с неверными деньгами на экране:
 *
 *  • зарплатный фонд считал начисленное без штрафов и требовал доплатить
 *    сумму, которой человек не был должен;
 *  • список выплат показывал «осталось выплатить» на закрытом начислении;
 *  • отмена выплаты сравнивала выплаченное с окладом и оставляла начисление
 *    закрытым, хотя премию человек так и не получил;
 *  • остаток к доплате считался от оклада, а итог — с корректировками, и
 *    полностью оплаченное начисление не попадало в выплаченные.
 *
 * Все четыре — не опечатки, а одно и то же решение, принятое заново каждым,
 * кто дописывал новый экран. Поэтому правило живёт здесь, а не повторяется:
 * ошибиться можно только один раз и сразу везде.
 */

/** Премия или штраф, приведённые к общему виду. */
export interface SignedAdjustment {
  type: 'BONUS' | 'PENALTY';
  amountMinor: number;
}

/** Премии со знаком плюс, штрафы — минус. */
export function adjustmentsTotal(items: readonly SignedAdjustment[]): number {
  return items.reduce(
    (sum, item) => sum + (item.type === 'PENALTY' ? -item.amountMinor : item.amountMinor),
    0,
  );
}

/**
 * Сколько человек получит: начисление плюс премии минус штрафы.
 *
 * Именно эту величину нужно сравнивать с выплаченным. Сравнение с одним
 * `netMinor` — источник всех четырёх перечисленных выше ошибок.
 */
export function payoutTotalNet(netMinor: number, items: readonly SignedAdjustment[]): number {
  return netMinor + adjustmentsTotal(items);
}

/**
 * Сколько осталось доплатить. Ниже нуля не опускается: переплату показывают
 * отдельно, а отрицательный остаток в интерфейсе читается как долг наоборот.
 */
export function payoutRemainder(
  netMinor: number,
  items: readonly SignedAdjustment[],
  paidMinor: number,
): number {
  return Math.max(0, payoutTotalNet(netMinor, items) - paidMinor);
}
